import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import React, { useEffect, useState, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  get_country_by_status,
  get_data_by_htscode,
  get_product_json_by_country,
  get_product_tariff_data,
  get_scrapped_countries,
  search_product,
} from "../services/apis";
import JsonTreeViewer from "./JsonTreeViewer";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function HTSLookup() {
  const location = useLocation();
  const [sourceCountry, setSourceCountry] = useState("");
  const [sourceCountryId, setSourceCountryId] = useState("");
  const [sourceCountryLabel, setSourceCountryLabel] = useState("");
  const [destCountryLabel, setDestCountryLabel] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [jsonData, setJsonData] = useState(null);
  const [selectedLeafCode, setSelectedLeafCode] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [htsData, setHtsData] = useState(null);
  const [isLoadingHts, setIsLoadingHts] = useState(true);
  const [goClicked, setGoClicked] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [currentsProduct, setCurrentsProduct] = useState("");
  const [value, setValue] = useState(0);
  const [isHtsData, setIsHtsData] = useState(false);
  const [inputProduct, setInputProduct] = useState("");
  const [originCountry, setOriginCountry] = useState([]);
  const [currentOrigin, setcurrentOrigin] = useState();
  const [currentOriginCode, setcurrentOriginCode] = useState();

  // New states for HIS Code Tab
  const [hisCodeOptions, setHisCodeOptions] = useState([]);
  const [loadingHisCodes, setLoadingHisCodes] = useState(false);
  const [selectedHisCode, setSelectedHisCode] = useState("");
  const [currentHisProduct, setCurrentHisProduct] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [querySearchTriggered, setQuerySearchTriggered] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const resultRef = useRef(null);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setJsonData(null);
    setSelectedLeafCode(null);
    setSelectedProduct(null);
    setHtsData(null);
    setGoClicked(false);
    setErrors({});

    // Reset states based on tab
    if (newValue === 1) {
      setHisCodeOptions([]);
      setSelectedHisCode("");
      setCurrentHisProduct(null);
    } else {
      setProducts([]);
      setCurrentsProduct("");
    }
  };

  const fetchCountries = async () => {
    try {
      const [originResponse, destinationResponse] = await Promise.all([
        get_country_by_status(),
        get_scrapped_countries(),
      ]);

      const originData = Array.isArray(originResponse?.data?.countries)
        ? originResponse.data.countries
        : [];

      const destinationData = Array.isArray(destinationResponse?.data?.data)
        ? destinationResponse.data.data
        : [];

      setOriginCountry(originData);

      const formattedCountries = destinationData.map((country) => ({
        ...country,
        id: country.id || country.country_code,
        label: country.country_name,
        value: country.country_code,
        currentId: country.id || country.country_code,
      }));

      setCountries(formattedCountries);
    } catch (error) {
      console.error("Failed to fetch countries:", error);
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (inputProduct?.trim().length >= 2) {
        setLoadingProducts(true);
        handleFetchProduct(inputProduct.trim());
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [inputProduct]);

  const handleFetchProduct = async (product) => {
    try {
      const response = await search_product(sourceCountry, product);
      const rawData = Array.isArray(response?.data?.his_categories)
        ? response.data.his_categories
        : response?.data?.data
          ? response?.data?.data
          : [];
      const formattedProducts = rawData.map((product) => ({
        label: product.description,
        value: product.reference,
      }));
      setProducts(formattedProducts);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // New function for HIS Code autocomplete
  const fetchHisCodeOptions = async (input) => {
    if (!input || input.length < 1) {
      setHisCodeOptions([]);
      return;
    }

    setLoadingHisCodes(true);
    try {
      const response = await fetch(
        `https://bapi.logistx.us/his_category_tree?code_prefix=${encodeURIComponent(input)}&country_code=1`,
      );
      const data = await response.json();

      const rawCategories = Array.isArray(data?.his_categories)
        ? data.his_categories
        : [];

      // Recursive function to flatten all nested items
      const flattenCategories = (items, result = []) => {
        items.forEach((item) => {
          result.push({
            label: `${item.code} - ${item.description || "No description"}`,
            his_code: item.code,
            product_name: item.description || "",
            reference: item.reference || item.code,
          });

          // Recursively add children if they exist
          if (Array.isArray(item.items) && item.items.length > 0) {
            flattenCategories(item.items, result);
          }
        });
        return result;
      };

      const formattedOptions = flattenCategories(rawCategories);

      setHisCodeOptions(formattedOptions);
    } catch (error) {
      console.error("Failed to fetch HIS codes:", error);
      setHisCodeOptions([]);
    } finally {
      setLoadingHisCodes(false);
    }
  };

  const handleGetJsonData = async () => {
    setSelectedLeafCode(null);
    const newErrors = {};

    if (!currentOrigin) newErrors.currentOrigin = "Source country is required";
    if (!sourceCountry)
      newErrors.sourceCountry = "Destination country is required";

    let productToUse = null;

    if (value === 0) {
      if (!currentsProduct) newErrors.currentsProduct = "Product is required";
      productToUse = currentsProduct;
    } else if (value === 1) {
      if (!selectedHisCode) newErrors.selectedHisCode = "HIS Code is required";
      productToUse = selectedHisCode;
    }

    setErrors(newErrors);

    if (currentOrigin === sourceCountryLabel) {
      toast.dismiss();
      toast.warn("Source and destination must differ.");
      return;
    }

    if (Object.keys(newErrors).length > 0) return;

    setIsHtsData(true);
    try {
      const { data, error } = await get_product_json_by_country(
        productToUse,
        sourceCountry,
      );

      if (error || !data) {
        setJsonData(null);
        setGoClicked(true);
        return;
      }

      const categories = data.his_categories || [];
      if (!Array.isArray(categories) || categories.length === 0) {
        setJsonData(null);
        setGoClicked(true);
        return;
      }

      setJsonData({ ...data, categories });
      setGoClicked(true);
    } catch (error) {
      console.error("Error fetching JSON:", error);
      setJsonData(null);
      setGoClicked(true);
    } finally {
      setIsHtsData(false);
    }
  };

  const runSearchWithValues = async (productToUse, destinationCountryCode) => {
    setSelectedLeafCode(null);
    setIsHtsData(true);
    try {
      const { data, error } = await get_product_json_by_country(
        productToUse,
        destinationCountryCode,
      );

      if (error || !data) {
        setJsonData(null);
        setGoClicked(true);
        return;
      }

      const categories = data.his_categories || [];
      if (!Array.isArray(categories) || categories.length === 0) {
        setJsonData(null);
        setGoClicked(true);
        return;
      }

      setJsonData({ ...data, categories });
      setGoClicked(true);
    } catch (error) {
      console.error("Error fetching JSON from query params:", error);
      setJsonData(null);
      setGoClicked(true);
    } finally {
      setIsHtsData(false);
    }
  };

  const findCountryByQuery = (countryList, name, code) => {
    const normalizedName = name?.trim().toLowerCase();
    const normalizedCode = code?.trim().toLowerCase();

    return countryList.find((country) => {
      const countryName = country.country_name?.trim().toLowerCase();
      const countryNameOnly = country.country_name
        ?.split(" - ")[0]
        ?.trim()
        ?.toLowerCase();
      const countryCode = String(country.country_code || "").toLowerCase();
      const countryId = String(country.id || "").toLowerCase();

      return (
        (normalizedName &&
          (countryName === normalizedName || countryNameOnly === normalizedName)) ||
        (normalizedCode &&
          (countryCode === normalizedCode || countryId === normalizedCode))
      );
    });
  };

  const formatLabel = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleDataByHtscode = async () => {
    if (!selectedLeafCode) return;
    setIsLoadingHts(true);
    try {
      const response = await get_product_tariff_data(
        selectedLeafCode,
        currentOriginCode,
        sourceCountry,
      );
      const data = response?.data;
      if (!data || !data.tariff) {
        console.warn("No HTS data returned");
        setHtsData(null);
        return;
      }

      const tariff = data.tariff;
      const exceptions = data.tariff_by_country_profile_exceptions || [];

      const parsedData = {
        hs_code: data.hs_code || selectedLeafCode,
        reporting_units: tariff.reporting_units?.trim() || "N/A",
        general: [],
        hierarchy: [],
        sections: [],
      };

      let currentSection = null;
      let sectionData = [];

      tariff.tariff_treatments?.lines?.forEach((line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        const isSectionHeader =
          trimmedLine.startsWith("Tariff Treatment") ||
          trimmedLine.startsWith("Tax 1") ||
          trimmedLine.startsWith("Tax 2") ||
          trimmedLine.startsWith("Tax 3") ||
          trimmedLine.startsWith("Tax 4") ||
          trimmedLine.startsWith("Tax 5") ||
          trimmedLine.startsWith("Tax 6") ||
          trimmedLine.startsWith("Tax 7") ||
          trimmedLine === "Country Notes" ||
          trimmedLine === "Detailed Duty and Taxes";

        if (isSectionHeader) {
          if (currentSection) {
            parsedData.sections.push({
              name: currentSection,
              data: sectionData,
            });
          }
          currentSection = trimmedLine;
          sectionData = [];
          return;
        }

        if (
          trimmedLine.match(/^\d{2}\s/) ||
          trimmedLine.match(/^\d{4}\s/) ||
          trimmedLine.match(/^\d{10}\s/)
        ) {
          parsedData.hierarchy.push({ description: trimmedLine });
          return;
        }

        if (trimmedLine.includes(":")) {
          const firstColonIndex = trimmedLine.indexOf(":");
          let key = trimmedLine.substring(0, firstColonIndex).trim();
          let value = trimmedLine.substring(firstColonIndex + 1).trim();

          if (key === "HS Code" && value.includes("Reporting Units")) {
            const parts = value.split("Reporting Units:");
            parsedData.general.push({ key: "HS Code", value: parts[0].trim() });
            parsedData.general.push({
              key: "Description",
              value: selectedProduct?.product_name || "N/A",
            });
            if (parts[1]) {
              parsedData.general.push({
                key: "Reporting Units",
                value: parts[1].trim(),
              });
            }
          } else if (currentSection) {
            sectionData.push({ key, value });
          } else {
            parsedData.general.push({ key, value });
          }
        }
      });

      if (currentSection) {
        parsedData.sections.push({
          name: currentSection,
          data: sectionData,
        });
      }

      const matchingException = exceptions.find((ex) => {
        return (
          ex.destination_country === sourceCountry &&
          ex.chapter_number === selectedLeafCode
        );
      });

      if (matchingException?.tariff_treatments) {
        const profile = matchingException.tariff_treatments;

        const tariffCalc = profile.Tariff_Calculation;
        if (tariffCalc) {
          parsedData.sections.forEach((section) => {
            if (section.name.startsWith("Tariff Treatment1")) {
              const calcItem = section.data.find(
                (item) => item.key === "Tariff Calculation",
              );
              if (calcItem) {
                calcItem.value = tariffCalc;
              }
            }
          });
        }

              // === SECTION 301 SUPPORT ===
        if (matchingException?.tariff_treatments?.Taxes?.[0]?.section_301) {
          const section301 = matchingException.tariff_treatments.Taxes[0].section_301;

          parsedData.sections.push({
            name: "Section 301",
            data: [
              { key: "Chapter 99", value: section301.chapter_99 || "" },
              { key: "Percentage", value: section301.percentage ? `${section301.percentage}%` : "" },
              { key: "HTS Code", value: section301.hts_code || "" },
            ].filter((item) => item.value),
          });
        }

        // === IMPROVED TAX HANDLING ===
        const exceptionTaxes = profile.Taxes || [];
        if (exceptionTaxes.length > 0) {
          parsedData.sections = parsedData.sections.filter((section) => {
            return !(
              section.name.startsWith("Tax") &&
              section.data.some((d) => d.key === "Tax Type" && d.value === "VAT")
            );
          });

          exceptionTaxes.forEach((tax, index) => {
            // Skip if it's only section_301 (no real tax data)
            if (tax.section_301 && Object.keys(tax).length === 1) {
              return;
            }

            const taxData = [
              { key: "Tax Name", value: tax.Tax_Name || "" },
              { key: "Tax Type", value: tax.Tax_Type || "" },
              { key: "Tax Calculation", value: tax.Tax_Calculation || "" },
              { key: "Tax Calculation Base", value: tax.Tax_Calculation_Base || "" },
              { key: "Formula", value: tax.Formula || "" },
              { key: "Notes", value: tax.Note || "" },
            ].filter((item) => item.value);   // Only add if value exists

            if (taxData.length > 0) {
              parsedData.sections.push({
                name: `Tax ${index + 1}`,
                data: taxData,
              });
            }
          });
        }
      }

      setHtsData(parsedData);
    } catch (error) {
      console.error("Error fetching HTS data:", error);
      setHtsData(null);
    } finally {
      setIsLoadingHts(false);
    }
  };

  useEffect(() => {
    if (selectedLeafCode) {
      handleDataByHtscode();
      setTabIndex(0);
    }
  }, [selectedLeafCode]);

  useEffect(() => {
    if (!isHtsData && jsonData?.categories?.length > 0 && resultRef.current) {
      const el = resultRef.current;
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset;
        const targetY = rect.top + scrollY - window.innerHeight / 2.5;
        window.scrollTo({
          top: targetY,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [isHtsData, jsonData]);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (querySearchTriggered || loadingCountries || originCountry.length === 0) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const searchMode =
      params.get("search_by") || params.get("active_tab") || params.get("tab");
    const hisCode =
      params.get("his_code") ||
      params.get("hs_code") ||
      params.get("hsh_code") ||
      params.get("hts_code");
    const product =
      params.get("product") ||
      params.get("product_name") ||
      params.get("search");

    let origin = findCountryByQuery(
      originCountry,
      params.get("origin_country"),
      params.get("origin_country_code"),
    );
    let destination = findCountryByQuery(
      countries,
      params.get("destination_country"),
      params.get("destination_country_code"),
    );
    if (!origin && !destination && !hisCode && !product && !searchMode) {
      return;
    }

    const shouldUseHisCode =
      searchMode?.toLowerCase() === "his_code" ||
      searchMode?.toLowerCase() === "hs_code" ||
      Boolean(hisCode);

    const selectedHtsCountry =
      params.get("hts_country") ||
      params.get("lookup_country") ||
      params.get("selected_country");

    if (selectedHtsCountry) {
      const selectedDestination = findCountryByQuery(
        countries,
        selectedHtsCountry,
        selectedHtsCountry,
      );

      if (selectedDestination) {
        destination = selectedDestination;
      }
    }

    setQuerySearchTriggered(true);
    setValue(shouldUseHisCode ? 1 : 0);
    setErrors({});
    setJsonData(null);
    setSelectedLeafCode(null);
    setHtsData(null);
    setGoClicked(false);

    if (origin) {
      setcurrentOrigin(origin.country_name || "");
      setcurrentOriginCode(origin.country_code || "");
      setDestCountryLabel(origin.country_name || "");
    }

    if (destination) {
      setSourceCountry(destination.country_code || "");
      setSourceCountryId(destination.id || "");
      setSourceCountryLabel(destination.country_name || "");
    }

    if (shouldUseHisCode && hisCode) {
      setSelectedHisCode(hisCode);
      setCurrentHisProduct({
        his_code: hisCode,
        label: hisCode,
        product_name: "",
        reference: hisCode,
      });
    } else if (product) {
      setCurrentsProduct(product);
      setInputProduct(product);
    }

    const productToSearch = shouldUseHisCode ? hisCode : product;
    if (origin && destination && productToSearch) {
      runSearchWithValues(productToSearch, destination.country_code);
    }
  }, [location.search, loadingCountries, originCountry, countries, querySearchTriggered]);

  return (
    <Box>
      <motion.div
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -70, opacity: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeOut",
          type: "spring",
          damping: 13,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 5 },
            py: 3,
            background: `linear-gradient(184.8deg, rgba(1, 189, 230, 0.6) 31.02%, rgba(168, 207, 216, 0.6) 96.15%), url("/background.svg")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            maxWidth: "100%",
            mx: "auto",
            mt: 0,
          }}
        >
          <Typography variant="h6" mb={4} mt={2} color="white">
            Get Started
          </Typography>
          <Typography
            variant="h4"
            fontWeight="bold"
            color="white"
            mb={4}
            width={550}
          >
            HTS codes made simple. Reach out for expert help.
          </Typography>

          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={6} sx={{ width: "100%" }}>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "inline-block",
                }}
              >
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="basic tabs example"
                  sx={{
                    color: "white",
                    "& .MuiTabs-indicator": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  <Tab
                    label="Search by product name"
                    {...a11yProps(0)}
                    sx={{
                      color: "white",
                      "&.Mui-selected": {
                        color: "white",
                      },
                    }}
                  />
                  <Tab
                    label="Search by HIS code"
                    {...a11yProps(1)}
                    sx={{
                      color: "white",
                      "&.Mui-selected": {
                        color: "white",
                      },
                    }}
                  />
                </Tabs>
              </Box>

              {/* Common Country Selection */}
              <Grid container spacing={3} my={3}>
                <Grid item xs={12} md={6} sx={{ width: "35%" }}>
                  <Autocomplete
                    fullWidth
                    options={originCountry}
                    getOptionLabel={(option) => option.country_name || ""}
                    value={
                      originCountry.find(
                        (c) => c.country_name === currentOrigin,
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      console.log("newValue-",newValue );
                      setcurrentOrigin(newValue ? newValue.country_name : "");
                      setcurrentOriginCode(newValue ? newValue.country_code : "");
                      setDestCountryLabel(
                        newValue ? newValue.country_name : "",
                      );
                      setSelectedLeafCode(null);
                      setJsonData(null);
                      setGoClicked(false);
                      setIsLoadingHts(true);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Country Of Origin"
                        variant="outlined"
                        required
                        error={!!errors.currentOrigin && !currentOrigin}
                        InputProps={{
                          ...params.InputProps,
                          sx: {
                            color: "white",
                            borderRadius: 2,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "white",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "white",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "white",
                            },
                          },
                        }}
                        InputLabelProps={{
                          style: { color: "white" },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6} sx={{ width: "35%" }}>
                  <Autocomplete
                    fullWidth
                    options={countries}
                    loading={loadingCountries}
                    getOptionLabel={(option) => option.label || ""}
                    value={
                      countries.find((c) => c.currentId === sourceCountryId) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      setSourceCountry(newValue ? newValue.value : "");
                      setSourceCountryId(newValue ? newValue.currentId : "");
                      setSourceCountryLabel(newValue ? newValue.label : "");
                      setSelectedLeafCode(null);
                      setJsonData(null);
                      setGoClicked(false);
                      setIsLoadingHts(true);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Destination Country"
                        variant="outlined"
                        required
                        error={!!errors.sourceCountry && !sourceCountry}
                        InputProps={{
                          ...params.InputProps,
                          sx: {
                            color: "white",
                            borderRadius: 2,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "white",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "white",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "white",
                            },
                          },
                        }}
                        InputLabelProps={{
                          style: { color: "white" },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Tab Panels */}
              <Grid container spacing={3} my={3}>
                <Grid item xs={12} md={6} sx={{ width: "35%" }}>
                  <CustomTabPanel value={value} index={0}>
                    <Autocomplete
                      fullWidth
                      freeSolo={true}
                      loading={loadingProducts}
                      options={products}
                      inputValue={inputProduct}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.label || ""
                      }
                      onInputChange={async (event, newInputValue, reason) => {
                        setInputProduct(newInputValue);
                        if (reason === "input") {
                          if (newInputValue.length >= 3) {
                            await handleFetchProduct(newInputValue);
                          } else {
                            setProducts([]);
                          }
                        }
                      }}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setCurrentsProduct(newValue);
                          setInputProduct(newValue);
                        } else {
                          setCurrentsProduct(newValue ? newValue.value : "");
                          setInputProduct(newValue ? newValue.label : "");
                        }
                        setSelectedLeafCode(null);
                        setJsonData(null);
                        setGoClicked(false);
                        setIsLoadingHts(true);
                        setProducts([]);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          label="Enter Product"
                          required
                          error={!!errors.currentsProduct && !currentsProduct}
                          InputProps={{
                            ...params.InputProps,
                            sx: {
                              color: "white",
                              borderRadius: 2,
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "white",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "white",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "white",
                                },
                            },
                          }}
                          InputLabelProps={{
                            style: { color: "white" },
                          }}
                        />
                      )}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel value={value} index={1}>
  <Autocomplete
    fullWidth
    freeSolo={true}                    // ← Important
    loading={loadingHisCodes}
    options={hisCodeOptions}
    value={hisCodeOptions.find(option => option.his_code === selectedHisCode) || null}
    inputValue={selectedHisCode}       // ← This keeps typed text
    getOptionLabel={(option) =>
      typeof option === "string" ? option : option.label || ""
    }
    onInputChange={async (event, newInputValue, reason) => {
      if (reason === "input") {
        setSelectedHisCode(newInputValue);   // Keep typed value
        await fetchHisCodeOptions(newInputValue);
      } else if (reason === "clear") {
        setSelectedHisCode("");
      }
    }}
    onChange={(event, newValue) => {
      if (typeof newValue === "string") {
        setSelectedHisCode(newValue);
        setCurrentHisProduct({
          his_code: newValue,
          label: newValue,
          product_name: "",
          reference: newValue,
        });
      } else if (newValue) {
        setSelectedHisCode(newValue.his_code);
        setCurrentHisProduct(newValue);
      } else {
        setSelectedHisCode("");
        setCurrentHisProduct(null);
      }
      setSelectedLeafCode(null);
      setJsonData(null);
      setGoClicked(false);
      setIsLoadingHts(true);
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        fullWidth
        variant="outlined"
        label="Enter HIS Code"
        required
        error={!!errors.selectedHisCode && !selectedHisCode}
        InputProps={{
          ...params.InputProps,
          sx: {
            color: "white",
            borderRadius: 2,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
          },
        }}
        InputLabelProps={{ style: { color: "white" } }}
      />
    )}
  />
</CustomTabPanel>
                </Grid>

                <Grid item sx={{ display: "flex", alignItems: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={handleGetJsonData}
                    sx={{
                      height: "56px",
                      borderRadius: "50px",
                      backgroundColor: "#00A9CE",
                      textTransform: "none",
                      fontWeight: "bold",
                    }}
                  >
                    {isHtsData ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography>Searching</Typography>
                        <CircularProgress
                          size={20}
                          sx={{ color: "white", ml: 1, fontSize: 20 }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography>Search</Typography>
                        <ArrowForwardIcon sx={{ fontSize: 20, ml: 0.5 }} />
                      </Box>
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </motion.div>

      {jsonData?.categories?.length > 0 && (
        <motion.div
          ref={resultRef}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Box
            sx={{
              my: 5,
              mx: 10,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "center",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Box
              sx={{
                flex: selectedLeafCode
                  ? { xs: "1 1 100%", md: "0 0 65%" }
                  : "1 1 100%",
                boxShadow: 3,
                padding: 3,
                borderRadius: 3,
                height: { xs: "auto", md: 600 },
                overflow: "auto",
                transition: "flex 0.3s ease",
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Search Results
              </Typography>

              {jsonData?.categories?.length > 0 ? (
                <JsonTreeViewer
                  data={jsonData.categories}
                  onLeafSelect={(code, node) => {
                    setSelectedLeafCode(code);
                    setSelectedProduct({
                      his_code: node.code || "",
                      product_name: node.description || "",
                      reference: node.reference || "",
                    });
                  }}
                />
              ) : (
                <div className="text-center text-gray-500">
                  -- No HTS Data Available --
                </div>
              )}
            </Box>

            {selectedLeafCode && (
              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "0 0 27%" },
                  boxShadow: 3,
                  padding: 3,
                  borderRadius: 3,
                  height: { xs: "auto", md: 600 },
                  overflow: "auto",
                  transition: "flex 0.3s ease",
                }}
              >
                {isLoadingHts ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <CircularProgress />
                  </Box>
                ) : htsData ? (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      Tariff Details
                    </Typography>

                    <Tabs
                      value={tabIndex}
                      onChange={handleTabChange}
                      textColor="inherit"
                      variant="scrollable"
                      scrollButtons
                      allowScrollButtonsMobile
                      TabIndicatorProps={{
                        style: { backgroundColor: "#00A9CE", marginBottom: 6 },
                      }}
                      sx={{
                        mb: 2,
                        "& .MuiTab-root": {
                          color: "#39b4cfff",
                          minWidth: "120px",
                        },
                        "& .Mui-selected": {
                          color: "#00A9CE !important",
                          fontWeight: 600,
                        },
                        "& .MuiTabs-scrollButtons": {
                          color: "#00A9CE",
                          padding: "0px",
                          width: "15px",
                          margin: "3px",
                        },
                        "& .MuiTabs-scrollButtons.Mui-disabled": {
                          opacity: 0.3,
                          color: "#00A9CE",
                        },
                      }}
                    >
                      <Tab label="General Info" />
                      <Tab label="Detailed Info" />
                      <Tab label="Miscellaneous Info" />
                    </Tabs>

                    {/* General Info */}
                    {tabIndex === 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {htsData.general?.map((item, index) => (
                          <Typography
                            key={index}
                            variant="subtitle2"
                            color="textSecondary"
                            sx={{ mb: 1, display: "flex", gap: 1 }}
                          >
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                color: "black",
                                width: 150,
                                fontSize: 14,
                              }}
                            >
                              {item.key}
                            </Typography>
                            :{" "}
                            <Typography
                              sx={{ fontSize: 14, ml: 1, maxWidth: "60%" }}
                            >
                              {item.value || ""}
                            </Typography>
                          </Typography>
                        ))}

                        <hr
                          style={{
                            margin: "6px 0px",
                            color: "gray",
                            backgroundColor: "gray",
                            height: "0.5px",
                          }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {htsData.hierarchy?.map((item, index) => (
                            <Typography
                              key={index}
                              variant="subtitle2"
                              color="textSecondary"
                              sx={{ mb: 1, display: "flex", gap: 1 }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  color: "black",
                                  width: 150,
                                  fontSize: 14,
                                }}
                              >
                                {item.description.split(" ")[0]}
                              </Typography>
                              :{" "}
                              <Typography
                                sx={{ fontSize: 14, ml: 1, maxWidth: "60%" }}
                              >
                                {item.description.split(" ").slice(1).join(" ")}
                              </Typography>
                            </Typography>
                          ))}
                        </Box>

                        <hr
                          style={{
                            margin: "6px 0px",
                            color: "gray",
                            backgroundColor: "gray",
                            height: "0.1px",
                          }}
                        />

                        {htsData.sections
                          ?.filter((s) => s.name === "Detailed Duty and Taxes")
                          .map((s, sIdx) => (
                            <Box key={`ddt-section-${sIdx}`} sx={{ mb: 2 }}>
                              {s.data
                                .filter(
                                  (item) =>
                                    item.key?.trim().toLowerCase() !==
                                    "country shipped from",
                                )
                                .map((item, idx) => (
                                  <Typography
                                    key={`ddt-${idx}`}
                                    variant="subtitle2"
                                    color="textSecondary"
                                    sx={{ mb: 1, display: "flex", gap: 1 }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: "bold",
                                        color: "black",
                                        width: 150,
                                        fontSize: 14,
                                      }}
                                    >
                                      {item.key}
                                    </Typography>
                                    :{" "}
                                    <Typography
                                      sx={{
                                        fontSize: 14,
                                        ml: 1,
                                        maxWidth: "60%",
                                      }}
                                    >
                                      {item.key === "Country of Origin"
                                        ? currentOrigin
                                        : item.key === "Country of Destination"
                                          ? sourceCountryLabel
                                          : item.value || ""}
                                    </Typography>
                                  </Typography>
                                ))}
                            </Box>
                          ))}
                      </Box>
                    )}

                    {/* Detailed Info */}
                    {tabIndex === 1 && (
                      <Box>
                        {htsData.sections
                          .filter((section) => {
                            if (
                              sourceCountryLabel !== "UNITED STATES - US" &&
                              section.name ===
                                "Tariff Treatment2: United States of America Rate"
                            ) {
                              return false;
                            }
                            return (
                              section.name !== "Detailed Duty and Taxes" &&
                              section.name !== "Country Notes"
                            );
                          })
                          .map((section, index) => {
                            if (section.name.startsWith("Tax")) {
                              const vatItems = section.data.filter(
                                (i) =>
                                  i.key === "Tax Type" && i.value === "VAT",
                              );
                              if (vatItems.length === 0) return null;
                            }

                            return (
                              <Box key={index} sx={{ mb: 3 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: "bold",
                                    mb: 1,
                                    color: "#000",
                                    fontSize: 16,
                                  }}
                                >
                                  {section.name.replace(
                                    "Tariff Treatment",
                                    "Tariff ",
                                  )}
                                </Typography>

                                {section.data
                                  .filter(
                                    (item) =>
                                      item.key?.trim().toLowerCase() !==
                                        "country shipped from" &&
                                      item.key !== "Calculation Base" &&
                                      item.key !== "Notes",
                                  )
                                  .map((item, idx) => (
                                    <Typography
                                      key={idx}
                                      variant="subtitle2"
                                      color="textSecondary"
                                      sx={{ mb: 1, display: "flex", gap: 1 }}
                                    >
                                      <Typography
                                        sx={{
                                          color: "black",
                                          width: 150,
                                          fontSize: 14,
                                        }}
                                      >
                                        {item.key}
                                      </Typography>
                                      :{" "}
                                      <Typography
                                        sx={{
                                          fontSize: 14,
                                          ml: 1,
                                          maxWidth: "60%",
                                        }}
                                      >
                                        {item.key === "Country of Destination"
                                          ? sourceCountryLabel
                                          : item.key === "Country of Origin"
                                            ? destCountryLabel
                                            : item.value || ""}
                                      </Typography>
                                    </Typography>
                                  ))}
                              </Box>
                            );
                          })}
                      </Box>
                    )}

                    {/* Miscellaneous Info */}
                    {tabIndex === 2 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {htsData.sections
                          .filter(
                            (section) =>
                              section.name.startsWith("Tax") &&
                              !section.data.some(
                                (item) =>
                                  item.key === "Tax Type" &&
                                  item.value === "VAT",
                              ),
                          )
                          .map((section, index) => (
                            <Box key={index} sx={{ mb: 3 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1,
                                  color: "#000",
                                  fontSize: 16,
                                }}
                              >
                                {section.name}
                              </Typography>
                              {section.data.map((item, idx) => (
                                <Typography
                                  key={idx}
                                  variant="subtitle2"
                                  color="textSecondary"
                                  sx={{ mb: 1, display: "flex", gap: 1 }}
                                >
                                  <Typography
                                    sx={{
                                      color: "black",
                                      width: 150,
                                      fontSize: 14,
                                    }}
                                  >
                                    {item.key}
                                  </Typography>
                                  :{" "}
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      ml: 1,
                                      maxWidth: "60%",
                                    }}
                                  >
                                    {item.value || ""}
                                  </Typography>
                                </Typography>
                              ))}
                            </Box>
                          ))}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography variant="body1" color="textSecondary">
                      -- No Tariff Data Available --
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </motion.div>
      )}

      {goClicked &&
        (!jsonData?.categories || jsonData.categories.length === 0) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginY: 5,
              fontSize: 12,
            }}
          >
            <Typography variant="h6" gutterBottom>
              - - No HTS Data available - -
            </Typography>
          </Box>
        )}
    </Box>
  );
}
